import { isProdEnv } from 'utils/common';

// management-api reports the whole MSSP -> Check Point Portal flow to Datadog as
// `@evt:migration.*` lines carrying `@batchId` as a facet (P81-136164). Nothing
// else reports per-tenant progress, so the log stream is the only place an
// operator can track a batch after it starts.
//
// Which Datadog org holds those logs depends on two things: production reports
// into the two regional orgs (US into datadoghq.com, every other region into
// datadoghq.eu), and everything that is not production - staging, the dev envs,
// a local run - reports into the shared staging org.
const DATADOG_ORG_PROD_US = 'app.datadoghq.com';
const DATADOG_ORG_PROD_NON_US = 'app.datadoghq.eu';
const DATADOG_ORG_NON_PROD = 'perimeter81-staging.datadoghq.com';

// What happened, to which tenant, at which step, and why it failed - enough to
// read a batch without opening a single line.
const COLUMNS = ['@evt', '@tenantId', '@step', '@errorRule'];

// Not URLSearchParams: it encodes a space as '+', which is only a space under
// form encoding. %20 reads as a space either way, so a query that grows a second
// term later cannot break on the encoding.
const toQueryString = (params: Record<string, string>): string =>
  Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');

// A batchId is a UUID, so it identifies the batch on its own - no `service:` or
// `@evt:` term adds anything next to it. Without one, though, the event name is
// the only thing keeping the stream to this flow: a bare `@batchId:*` would pull
// in every line in the org that happens to carry a batchId.
const queryFor = (batchId?: string): string =>
  batchId ? `@batchId:${batchId}` : '@evt:migration.*';

// The query is org-independent either way: `@batchId` and `@evt` are both fields
// the service writes, not tags that vary by environment.
const datadogOrg = (regionCode: string): string => {
  if (!isProdEnv()) return DATADOG_ORG_NON_PROD;
  return regionCode === 'US' ? DATADOG_ORG_PROD_US : DATADOG_ORG_PROD_NON_US;
};

/** `regionCode` is a RegionContext code (US, EU, IN, AU, CA). */
export const buildMigrationLogsUrl = (
  regionCode: string,
  batchId?: string
): string => {
  const query = toQueryString({
    query: queryFor(batchId),
    cols: COLUMNS.join(','),
    index: '*',
    messageDisplay: 'inline',
    refresh_mode: 'sliding',
    stream_sort: 'desc',
    viz: 'stream',
    live: 'true'
  });

  return `https://${datadogOrg(regionCode)}/logs?${query}`;
};
